import React, { useState } from 'react';
import {
  Box, TextField, InputAdornment, FormControl, InputLabel, Select,
  MenuItem, Button, Chip, Paper, Typography, IconButton, Collapse
} from '@mui/material';
import {
  Search, FilterList, Clear, Sort, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { status } from '../../theme/colors';

/**
 * Project Filters Component
 * Enhanced filtering and sorting for projects
 */
const ProjectFilters = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  sortBy,
  onSortChange,
  additionalFilters,
  onAdditionalFiltersChange,
  onClearFilters,
  resultCount
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleClearAll = () => {
    onSearchChange('');
    onStatusChange('all');
    onSortChange('recent');
    onAdditionalFiltersChange({
      location: '',
      minBudget: '',
      maxBudget: '',
      dateFrom: '',
      dateTo: ''
    });
    onClearFilters();
  };

  const hasActiveFilters = () => {
    return searchTerm ||
           statusFilter !== 'all' ||
           sortBy !== 'recent' ||
           additionalFilters.location ||
           additionalFilters.minBudget ||
           additionalFilters.maxBudget ||
           additionalFilters.dateFrom ||
           additionalFilters.dateTo;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter !== 'all') count++;
    if (additionalFilters.location) count++;
    if (additionalFilters.minBudget) count++;
    if (additionalFilters.maxBudget) count++;
    if (additionalFilters.dateFrom) count++;
    if (additionalFilters.dateTo) count++;
    return count;
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
      {/* Main Filter Row */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <TextField
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ flex: '1 1 300px', minWidth: '250px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Sort By */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => onSortChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <Sort />
              </InputAdornment>
            }
          >
            <MenuItem value="recent">Most Recent</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="budget_high">Budget (High to Low)</MenuItem>
            <MenuItem value="budget_low">Budget (Low to High)</MenuItem>
            <MenuItem value="progress_high">Progress (High to Low)</MenuItem>
            <MenuItem value="progress_low">Progress (Low to High)</MenuItem>
            <MenuItem value="title_asc">Title (A-Z)</MenuItem>
            <MenuItem value="title_desc">Title (Z-A)</MenuItem>
          </Select>
        </FormControl>

        {/* Status Filter */}
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => onStatusChange(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="planning">Planning</MenuItem>
            <MenuItem value="open_for_bids">Open for Bids</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="on_hold">On Hold</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        {/* Advanced Filters Toggle */}
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          sx={{
            borderColor: getActiveFilterCount() > 0 ? status.info : undefined,
            color: getActiveFilterCount() > 0 ? status.info : undefined
          }}
        >
          More Filters
          {getActiveFilterCount() > 0 && (
            <Chip
              label={getActiveFilterCount()}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                backgroundColor: status.info,
                color: 'white',
                fontWeight: 600
              }}
            />
          )}
        </Button>
      </Box>

      {/* Advanced Filters */}
      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#666' }}>
            Advanced Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Location"
              placeholder="City, State, or Country"
              value={additionalFilters.location}
              onChange={(e) => onAdditionalFiltersChange({
                ...additionalFilters,
                location: e.target.value
              })}
              sx={{ flex: '1 1 200px' }}
            />
            <TextField
              label="Min Budget"
              type="number"
              placeholder="0"
              value={additionalFilters.minBudget}
              onChange={(e) => onAdditionalFiltersChange({
                ...additionalFilters,
                minBudget: e.target.value
              })}
              sx={{ flex: '1 1 150px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            <TextField
              label="Max Budget"
              type="number"
              placeholder="∞"
              value={additionalFilters.maxBudget}
              onChange={(e) => onAdditionalFiltersChange({
                ...additionalFilters,
                maxBudget: e.target.value
              })}
              sx={{ flex: '1 1 150px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            <TextField
              label="Date From"
              type="date"
              value={additionalFilters.dateFrom}
              onChange={(e) => onAdditionalFiltersChange({
                ...additionalFilters,
                dateFrom: e.target.value
              })}
              sx={{ flex: '1 1 150px' }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date To"
              type="date"
              value={additionalFilters.dateTo}
              onChange={(e) => onAdditionalFiltersChange({
                ...additionalFilters,
                dateTo: e.target.value
              })}
              sx={{ flex: '1 1 150px' }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </Collapse>

      {/* Results Count and Clear Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
          Showing {resultCount} {resultCount === 1 ? 'project' : 'projects'}
        </Typography>
        {hasActiveFilters() && (
          <Button
            size="small"
            startIcon={<Clear />}
            onClick={handleClearAll}
            sx={{ color: '#666' }}
          >
            Clear all filters
          </Button>
        )}
      </Box>

      {/* Active Filter Chips */}
      {hasActiveFilters() && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {searchTerm && (
            <Chip
              label={`Search: "${searchTerm}"`}
              onDelete={() => onSearchChange('')}
              size="small"
            />
          )}
          {statusFilter !== 'all' && (
            <Chip
              label={`Status: ${statusFilter.replace('_', ' ')}`}
              onDelete={() => onStatusChange('all')}
              size="small"
            />
          )}
          {additionalFilters.location && (
            <Chip
              label={`Location: ${additionalFilters.location}`}
              onDelete={() => onAdditionalFiltersChange({ ...additionalFilters, location: '' })}
              size="small"
            />
          )}
          {additionalFilters.minBudget && (
            <Chip
              label={`Min: $${additionalFilters.minBudget}`}
              onDelete={() => onAdditionalFiltersChange({ ...additionalFilters, minBudget: '' })}
              size="small"
            />
          )}
          {additionalFilters.maxBudget && (
            <Chip
              label={`Max: $${additionalFilters.maxBudget}`}
              onDelete={() => onAdditionalFiltersChange({ ...additionalFilters, maxBudget: '' })}
              size="small"
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ProjectFilters;
